import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { AuthorshipSchema, CurrencySchema, IsoDateSchema, MessageSchema, } from './primitives.js';
const c = initContract();
/**
 * FIN-05 — Currency Exchange (`fx`, accounting area per 16.2.2 row 6:
 * "Chart of accounts, journal entries, vouchers, foreign exchange, fixed
 * assets").
 *
 * THE convention, cited not restated (BR-FIN-05-01/-02, PART 18 F-10):
 * a rate is how many units of THAT currency equal ONE unit of the base;
 * base→foreign multiplies, foreign→base divides. The arithmetic lives in
 * @palaccount/money's fx helpers — nothing else may restate it.
 *
 * Permission mapping (FIN-05 §9 through the 12×5 grid):
 *   read the table, the history, the preview   = accounting.view
 *   add a currency                             = accounting.create
 *   change a manual rate / a row's source /
 *     Fetch Live Rates (currency maintenance)  = accounting.edit
 *   post a revaluation (a journal posts)       = accounting.approve (AP-2)
 *   set the base currency (company-wide,
 *     one-time — §2: the Owner or an Admin)    = admin.edit
 */
// ── The currency catalog (§5a's seven — SUGGESTED, not a fence) ─────────────
/**
 * BR-MD-11-01's shape, once, so the three places that enforce it cannot drift:
 * this schema, `FxService.addCurrency`, and the database's own
 * `CHECK (currency ~ '^[A-Z]{3}$')` on every currency-bearing column
 * (20260908100000). The rule is "exactly three CAPITAL letters" — lower case
 * is refused rather than folded, because the database CHECK refuses it and a
 * server that silently uppercased would be the only thing standing between a
 * caller and a constraint violation it could not read.
 */
export const CURRENCY_CODE_RE = /^[A-Z]{3}$/;
/**
 * FIN-05 §5a field 1: "Seven choices: USD, EUR, GBP, AED, SAR, ILS, CAD."
 *
 * ── THESE SEVEN ARE A SUGGESTION SINCE D16 (26 Aug 2026) ───────────────────
 *
 * They were a CLOSED LIST until then: `addCurrency` refused any code this
 * array did not hold. That read §5a's "Seven choices" literally and made the
 * specification contradict itself, because IMP-01 §5 field 14 gives the Import
 * Order screen SIX OTHERS — "USD, EUR, GBP, CNY, JPY, AED" — and each list
 * holds two codes the other does not. The product therefore refused to create
 * the JPY one half of its own spec lists, while the CNY its own worked example
 * is denominated in reached the database only because `imports.service.ts`
 * asks the currency guard for `switch-off-only`.
 *
 * The owner ruled the list OPEN, on the evidence that MD-11 §14 had already
 * written the error message for a code somebody TYPED (ERR-MD-11-01, below) —
 * a spec that writes the error for typing a code intended codes to be typed.
 *
 * So the array keeps two jobs and loses one:
 *   KEEPS   the name, Arabic name and symbol a well-known code is created
 *           with, so adding EUR still produces "Euro" and "€" rather than
 *           "EUR" and "EUR".
 *   KEEPS   the suggestion list the Add-currency box offers (`addable`).
 *   LOSES   being the gate. `CURRENCY_CODE_RE` is the gate now.
 */
export const CURRENCY_CATALOG = [
    { code: 'USD', name: 'US Dollar', nameAr: 'دولار أمريكي', symbol: '$' },
    { code: 'EUR', name: 'Euro', nameAr: 'يورو', symbol: '€' },
    { code: 'GBP', name: 'British Pound', nameAr: 'جنيه إسترليني', symbol: '£' },
    { code: 'AED', name: 'UAE Dirham', nameAr: 'درهم إماراتي', symbol: 'د.إ' },
    { code: 'SAR', name: 'Saudi Riyal', nameAr: 'ريال سعودي', symbol: 'ر.س' },
    { code: 'ILS', name: 'Israeli Shekel', nameAr: 'شيكل', symbol: '₪' },
    { code: 'CAD', name: 'Canadian Dollar', nameAr: 'دولار كندي', symbol: 'C$' },
];
export function catalogCurrency(code) {
    return CURRENCY_CATALOG.find((entry) => entry.code === code);
}
// ── Fixed sentences ─────────────────────────────────────────────────────────
export const FX_MESSAGES = {
    /** ERR-FIN-05-01 / BR-FIN-05-03. */
    liveRateReadOnly: 'A Live API rate is read-only. Switch the row to Manual first.',
    /**
     * BR-FIN-05-13 ("must refuse a duplicate code") states the rule and no
     * sentence; MD-11 §14 states the sentence, as ERR-MD-11-02: "EUR already
     * exists." So the wording comes from there and the code is named, which is
     * what makes the message useful when several are being added at once.
     */
    duplicateCurrency: (code) => `${code} already exists.`,
    /**
     * ERR-MD-11-01, VERBATIM — and the sentence D16's whole argument turns on:
     * "There is a sentence in it, 'A currency code is exactly three letters,
     * like USD.', currently unused, which only makes sense for a code somebody
     * typed. A spec that wrote the error for typing a code intended codes to be
     * typed."
     *
     * IT WAS UNUSED FOR A REASON, AND THE REASON IS NOW GONE.
     * `packages/db/src/schema/currencies.ts` recorded the debt precisely —
     * "ERR-MD-11-01's sentence HAS NO SITE and is not built: FIN-05's
     * Add-currency picks from the closed CURRENCY_CATALOG, so a two-letter code
     * cannot be typed... A free-text code field added later owes the sentence."
     * D16 added the free-text field; this is the sentence being paid.
     *
     * BR-MD-11-01 is the rule ("exactly three capital letters and unique"); this
     * is the first of its two halves. The second — uniqueness — already had its
     * own sentence in `duplicateCurrency` above.
     *
     * NOT to be confused with `unknownCurrency` below, which they now split
     * cleanly between them: this one answers a code that is not SHAPED like a
     * currency, and that one answers a code that is shaped right but is not in
     * the tenant's list. Before D16 one sentence did both jobs on the create
     * path and did the first one badly — a typed `JP` was told to "choose one of
     * the offered currencies" when nothing was being offered.
     */
    codeShape: 'A currency code is exactly three letters, like USD.',
    /**
     * A code the tenant's own `currencies` table does not hold. Since D16 this
     * is no longer said on the CREATE path — creating is how a code gets into
     * that table — and its callers are the ones that CONSUME a code: the guard's
     * `strict` mode, `setBase`, and the rate routes.
     */
    unknownCurrency: 'Choose one of the offered currencies.',
    /** BR-FIN-05-07. */
    baseHasNoRate: 'The base currency has no rate against itself and cannot be added to the table.',
    /**
     * BR-MD-11-07 / ERR-MD-11-04, verbatim: "A rate must be above zero."
     * (FIN-05 §15 agrees that zero and negative rates are meaningless but quotes
     * no sentence, so MD-11's is the literal.)
     */
    rateMustBePositive: 'A rate must be above zero.',
    /** BR-FIN-05-06. */
    baseLocked: 'The base currency is set once, company-wide. It cannot change after any document exists, because the stored rates would mean the wrong thing.',
    /**
     * ERR-MD-11-07, the sentence verbatim and PARAMETERISED where MD-11 §14
     * parameterises it: "EUR is used on 214 documents and cannot be deleted.
     * Switch it off instead."
     *
     * This replaced a placeholder ("This currency is in use and cannot be
     * removed.") that had no caller AND could not have satisfied BR-DATA-04 if
     * it had one — "a refused delete must name what is in the way and how many
     * there are. It must not say only 'cannot delete'." The count is the half
     * the rule is about, and the second sentence is the half BR-DATA-05 is
     * about: it points at the switch-off, which now exists.
     */
    currencyInUse: (code, documents) => `${code} is used on ${documents} ${documents === 1 ? 'document' : 'documents'} and cannot be deleted. Switch it off instead.`,
    /**
     * §17.3.2's currency row refuses a delete when "any document uses it, OR IT
     * IS THE BASE CURRENCY", and quotes the second half's sentence itself: "The
     * base currency cannot be deleted."
     */
    baseCannotBeDeleted: 'The base currency cannot be deleted.',
    /**
     * ERR-MD-11-08, the sentence verbatim: "USD is the base currency and cannot
     * be switched off." The code is the parameter MD-11 §14's row varies.
     * BR-MD-11-06 is the rule; `currencies_base_active_check` is the same rule
     * at the database, so this is the sentence rather than the only defence.
     */
    baseCannotSwitchOff: (code) => `${code} is the base currency and cannot be switched off.`,
    /** ERR-FIN-05-03 / BR-FIN-05-12. */
    nothingTicked: 'Tick at least one account before posting.',
    /**
     * BR-FIN-05-12's server truth: a repeat of the same selection restates
     * nothing — ERR-FIN-05-04, where the button greys after the first press.
     */
    nothingToPost: 'The ticked accounts are already stated at the current rate. There is nothing to post.',
    /** BR-FIN-05-11. */
    gainLossAccountKind: 'The gain or loss must be posted to a revenue or expense account.',
    /** ERR-DSH-04-04's sentence shape, reused where a rate is missing. */
    noRateFor: (code, date) => `No exchange rate for ${code} on or before ${date}. Figures in that currency are not included.`,
    /** §15 Partial: an account with no booked base value has no original rate. */
    noBookedRate: 'This account has no booked rate and cannot be revalued.',
    /**
     * GEN-114 / BR-MD-11-10 / ERR-MD-11-06, the sentence verbatim including
     * where to go — MD-11 §14's row and GEN-114's quote are the same sentence:
     * "A document must not be confirmed in a currency that has no rate on or
     * before its date. The message must be: 'There is no EUR rate for
     * 2026-07-07. Add one in Accounting & Finance > Vouchers & FX.'"
     */
    noRateToFreeze: (code, date) => `There is no ${code} rate for ${date}. Add one in Accounting & Finance > Vouchers & FX.`,
    /**
     * Wave 4D — a typed (overridden) rate on a document in the company's OWN
     * currency. One unit of the base is one unit of the base (BR-FIN-05-07), so
     * the only rate such a document can carry is 1.
     */
    baseRateIsOne: (code) => `${code} is the company's base currency, so its rate is always 1.`,
    /**
     * BR-MD-11-14, the sentence verbatim: "When the newest rate is more than 7
     * days old, confirming a document in that currency must warn: 'The newest
     * EUR rate is from 2026-06-28. Continue with 0.9180?'"
     */
    staleRate: (code, effectiveDate, rate) => `The newest ${code} rate is from ${effectiveDate}. Continue with ${rate}?`,
    /**
     * GEN-115's disclosure, for a screen whose rows each carry their OWN frozen
     * rate: "Any screen that converts money between currencies must show a
     * one-line disclosure directly above the numbers, naming the target currency
     * and the date of the rates used." There is no single date here — that is
     * the whole point of EC-19-10 — so it names the basis instead of inventing
     * one, which is the only reading that is both compliant and true. Grey and
     * informational (GEN-117), hidden when nothing converted (GEN-116).
     */
    convertedAtFrozenRates: (baseCode) => `Converted to ${baseCode} · each document at the rate frozen when it was confirmed`,
    /**
     * D19's VISIBLE EXCLUSION, in GEN-115/GEN-116's shape — a one-line
     * disclosure directly above the numbers, hidden entirely when nothing was
     * left out.
     *
     * D19 rules that a foreign line posted before base-currency equivalents
     * existed is "discarded as pre-launch data": it has no document this table
     * can reach and no rate of its own, so a figure invented for it would be an
     * undefendable number presented as a defendable one. That discard is only
     * HONEST if the reader is told, because a total that quietly omits something
     * — balancing, reconciling, under a green indicator — is the exact defect
     * D19 exists to end. Ending it by leaving something else out silently would
     * be absurd.
     *
     * `detail` is the count per currency ("2 in EUR"), because BR-DATA-04's
     * principle applies to any refusal or omission that names a population: say
     * what is in the way and HOW MANY there are, never only that something is.
     *
     * No company created after this release can ever see this sentence — nothing
     * can enter that population again once every posting door writes the
     * equivalent.
     */
    excludedPreLaunchLines: (baseCode, detail) => `Some journal lines posted before this company recorded base-currency values (${detail}) ` +
        `cannot be stated in ${baseCode} and are not included.`,
    /**
     * Per-document currency (owner decision, 30 Oct 2026) — a document made
     * FROM another keeps the source's currency: a credit note from an invoice,
     * a purchase return from a bill, an invoice or order converted from a
     * quotation or order. Names the source and its currency. No rule id: the
     * behaviour is the owner's ruling, not a spec sentence.
     */
    sourceCurrencyLocked: (sourceCode, currency) => `${sourceCode} is in ${currency}. A document made from it must be in ${currency} too.`,
    /**
     * Per-document currency — a receipt, payment or credit note settles only
     * documents in its OWN currency. Names the document, its currency and the
     * settling document's currency.
     */
    settleCurrencyMismatch: (code, documentCurrency, settleCurrency) => `${code} is in ${documentCurrency} and cannot be settled in ${settleCurrency}.`,
};
/**
 * D46 — the currency family's structured refusals: key + params beside the
 * composed English sentence, so a refusal that names a currency code, a count
 * or a date reaches an Arabic reader in Arabic. One builder per parameterised
 * member the API raises inside an HTTP exception body — the raise sites throw
 * THESE, and each `message` is composed by the `FX_MESSAGES` twin so the two
 * cannot drift (parity pinned member by member in
 * `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * The four other parameterised FX members are deliberately NOT here: none is
 * an HTTP refusal body. `noRateFor` and `excludedPreLaunchLines` and
 * `convertedAtFrozenRates` ride report footnotes/disclosure fields, and
 * `staleRate` rides a 2xx response's `rateWarning` field (all four recorded
 * in the census's NOT_REFUSAL_BODIES roster).
 *
 * `currencyInUse` travels the RAW count; the dictionaries pluralise the noun
 * (en mirrors the sentence's own one/other ternary, ar carries the full
 * six-category set). Every other param is a display value — a code the reader
 * typed or a document's own ISO date — embedded verbatim in both languages.
 * No member carries a select token.
 */
export const FX_REFUSALS = {
    duplicateCurrency: (code) => ({
        message: FX_MESSAGES.duplicateCurrency(code),
        key: 'fx.errDuplicateCurrency',
        params: { code },
    }),
    currencyInUse: (code, documents) => ({
        message: FX_MESSAGES.currencyInUse(code, documents),
        key: 'fx.errCurrencyInUse',
        params: { code, documents },
    }),
    baseCannotSwitchOff: (code) => ({
        message: FX_MESSAGES.baseCannotSwitchOff(code),
        key: 'fx.errBaseCannotSwitchOff',
        params: { code },
    }),
    noRateToFreeze: (code, date) => ({
        message: FX_MESSAGES.noRateToFreeze(code, date),
        key: 'fx.errNoRateToFreeze',
        params: { code, date },
    }),
    baseRateIsOne: (code) => ({
        message: FX_MESSAGES.baseRateIsOne(code),
        key: 'fx.errBaseRateIsOne',
        params: { code },
    }),
    // Per-document currency (owner decision, 30 Oct 2026). All display params.
    sourceCurrencyLocked: (sourceCode, currency) => ({
        message: FX_MESSAGES.sourceCurrencyLocked(sourceCode, currency),
        key: 'fx.errSourceCurrencyLocked',
        params: { code: sourceCode, currency },
    }),
    settleCurrencyMismatch: (code, documentCurrency, settleCurrency) => ({
        message: FX_MESSAGES.settleCurrencyMismatch(code, documentCurrency, settleCurrency),
        key: 'fx.errSettleCurrencyMismatch',
        params: { code, documentCurrency, settleCurrency },
    }),
};
/**
 * BR-MD-11-14's threshold, as a number, so the rule and the test that pins it
 * cannot drift apart: "when the newest rate is MORE THAN 7 DAYS OLD". More
 * than, not at least — a rate exactly 7 days old is still current, and the
 * boundary is the half of this rule that is easy to get wrong.
 */
export const STALE_RATE_DAYS = 7;
// ── Schemas ─────────────────────────────────────────────────────────────────
/** Four decimals, > 0 (FIN-05 §15: "Rates are held to four decimals"). */
export const RateStringSchema = z
    .string()
    .regex(/^\d{1,11}(\.\d{1,4})?$/, 'Rates are decimal strings with at most 4 decimals')
    .refine((value) => !/^0+(\.0+)?$/.test(value), FX_MESSAGES.rateMustBePositive);
/**
 * THE FROZEN RATE, ON THE WIRE — the five fields every document that can be
 * raised in a foreign currency carries, spread into its own response schema.
 *
 * One definition rather than nine copies, for the same reason the database
 * has one `frozenRateColumns`: nine hand-written copies is nine chances for
 * one document to quietly stop reporting its "as of" date.
 *
 *   exchangeRate     the FROZEN figure (GEN-109a: units of the document's own
 *                    currency per ONE base unit). NULL while the document is a
 *                    draft — BR-MD-11-11: "a draft document re-reads the newest
 *                    rate on or before its own date every time it is opened",
 *                    so a draft has no frozen rate and must not pretend to.
 *   rateEffectiveOn  BR-SAL-04-09's "as of" date — which dated row the figure
 *                    came from. NULL on a base-currency document (no rate row).
 *   baseCurrency     what the company reports in, so the caller can render
 *                    "≈ 54,450.00 USD" without a second request.
 *   baseTotal        the document's grand total converted AT THE FROZEN RATE
 *                    (F-10: foreign→base DIVIDES). NULL when no rate is frozen.
 *   rateWarning      BR-MD-11-14's sentence when the rate being applied is more
 *                    than STALE_RATE_DAYS old, else null. It is a warning, not
 *                    a refusal: the refusal is GEN-114's `noRateToFreeze`.
 */
export const FrozenRateFieldsSchema = z.object({
    exchangeRate: z.string().nullable(),
    rateEffectiveOn: IsoDateSchema.nullable(),
    baseCurrency: z.string(),
    baseTotal: z.string().nullable(),
    rateWarning: z.string().nullable(),
});
/** The five fields as a plain object, for `.extend()` / spread into `.strict()` shapes. */
export const frozenRateFields = FrozenRateFieldsSchema.shape;
/**
 * WAVE 4D — THE TYPED RATE ON A DRAFT, on the wire.
 *
 * The seven documents that freeze a rate at confirmation (sales invoice,
 * quotation, sales order, credit note, bill, purchase order, purchase return)
 * also accept one TYPED by the user while the document is a draft:
 *
 *   exchangeRate (input)  a RateString — stored on the draft's own
 *                         `exchange_rate` with `rate_override = true`, and it
 *                         is the figure the freeze KEEPS instead of reading
 *                         the table. On a PATCH, null clears it: the draft
 *                         goes back to re-reading the market rate
 *                         (BR-MD-11-11). A base-currency document refuses
 *                         anything but 1 (`FX_REFUSALS.baseRateIsOne`).
 *   rateOverride          true while the stored rate is a typed one — on a
 *                         draft and, after posting, for ever (the posted row
 *                         is locked, so the flag records how the rate was
 *                         chosen).
 *   marketRate/On         what the rate TABLE says for the document's own
 *                         date — the "market rate X as of D" hint beside the
 *                         input. Drafts only; null once confirmed (the post
 *                         audit row keeps the market figure a typed rate
 *                         replaced) and null when the currency has no rate.
 *                         `marketRateOn` is null for the base currency.
 */
export const RateOverrideResponseSchema = z.object({
    rateOverride: z.boolean(),
    marketRate: z.string().nullable(),
    marketRateOn: IsoDateSchema.nullable(),
});
export const rateOverrideFields = RateOverrideResponseSchema.shape;
/** The CREATE spelling: absent = the market rate. */
export const rateOverrideInputFields = {
    exchangeRate: RateStringSchema.optional(),
};
/** The PATCH spelling: absent = untouched, null = back to the market rate. */
export const rateOverridePatchFields = {
    exchangeRate: RateStringSchema.nullable().optional(),
};
/**
 * THE DOCUMENT'S OWN CURRENCY, on the wire (owner decision, 30 Oct 2026).
 *
 * The same seven documents may each pick their OWN currency; the partner's
 * currency is now only the DEFAULT. The code is checked against the tenant's
 * currency list (`assertCurrencyUsable`, BR-DATA-06).
 *
 *   CREATE  absent = the partner's currency. A document made FROM another
 *           (credit note from an invoice, return from a bill, bill from an
 *           order) keeps the source's currency and refuses any other
 *           (`FX_REFUSALS.sourceCurrencyLocked`, `BILL_REFUSALS.currencyMismatch`).
 *   PATCH   absent = untouched. Draft only. A CHANGED currency clears a typed
 *           exchange rate (the rate belonged to the old currency) unless the
 *           same request types a new one. A document linked to a source
 *           document cannot change it.
 */
export const documentCurrencyInputFields = {
    currency: CurrencySchema.optional(),
};
/** The PATCH spelling — see `documentCurrencyInputFields`. */
export const documentCurrencyPatchFields = {
    currency: CurrencySchema.optional(),
};
export const CurrencyRowSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    nameAr: z.string().nullable(),
    symbol: z.string(),
    /** §5c: manual rows survive a fetch; live rows are refreshed by it. */
    source: z.enum(['manual', 'live']),
    /**
     * MD-11 field 6 / §7's Active↔Inactive pair. False = "kept for history":
     * old documents keep working, and the row is not offered on anything new
     * (BR-DATA-06). Never false on the base row (BR-MD-11-06).
     */
    isActive: z.boolean(),
    /** The rate effective today, or null when none exists yet. */
    rate: z.string().nullable(),
    /** BR-FIN-05-14: when the current rate applies from. */
    effectiveDate: IsoDateSchema.nullable(),
    /** The Last updated stamp of the current rate. */
    lastUpdatedAt: z.string().nullable(),
    /** BR-FIN-05-05: true when the current rate was hand-typed — "(manual)". */
    lastUpdatedManual: z.boolean(),
    // MD-RULE-5's six fields — the CURRENCY RECORD's own authorship, which rule-coverage:ignore
    // `lastUpdatedAt` above is not: that is the current RATE row's stamp,
    // and it moves on every fetch while the record sits unchanged.
    ...AuthorshipSchema.shape,
})
    .strict();
export const CurrencyTableSchema = z
    .object({
    base: z
        .object({ code: z.string(), name: z.string(), symbol: z.string() })
        .strict(),
    /**
     * Never contains the base (BR-FIN-05-07), and — unless the caller asks
     * for `includeInactive` — never contains a switched-off row either
     * (BR-DATA-06: "must stop being offered on new ones"). This IS the
     * currency picker: the journal editor's per-line currency select reads
     * `rows` directly, so the filter here is what makes the switch-off mean
     * something rather than being a flag nobody consults.
     */
    rows: CurrencyRowSchema.array(),
    /**
     * Codes still addable from the seven-choice catalog.
     *
     * Computed from EVERY row the tenant has, active or not — deliberately
     * ignoring the filter above. Otherwise switching EUR off would put EUR
     * back in `addable`, and the add would come back 409 "EUR already
     * exists.": a dead end where the answer is the switch-on the maintenance
     * screen is already showing. It is the same argument `idsIn` makes in
     * platform/picker-search.ts — a picker filters, a resolve does not.
     */
    addable: z.string().array(),
    /** BR-FIN-05-06: whether the base may still change (no document yet). */
    baseLocked: z.boolean(),
})
    .strict();
/**
 * D16's open list, on the wire.
 *
 * `code` IS DELIBERATELY UNVALIDATED HERE, and that is a change from
 * `z.string().length(3)`. The shape rule now carries a sentence the spec
 * wrote — ERR-MD-11-01, "A currency code is exactly three letters, like USD."
 * — and a zod violation does not answer with it: it answers with ts-rest's
 * body-validation shape, which no screen renders and no user can act on. The
 * error MD-11 §14 quotes is literally the two-letter case, so the two-letter
 * case is exactly the one that must reach `FxService.addCurrency` and be
 * refused in the spec's own words. Validating here would intercept it.
 *
 * `name` and `symbol` are new with D16 and both optional, because an open list
 * means a code with no catalog entry behind it:
 *   - one of the seven, nothing supplied  →  "Euro", "€"  (unchanged)
 *   - a typed code with a name supplied   →  what the user typed
 *   - a typed code with nothing supplied  →  the code, for both (MD-11 field
 *     3's own fallback, "when empty, the code is used instead", extended to
 *     the name because the alternative is a blank NOT NULL column)
 *
 * They matter more than they look, because THERE IS NO RENAME ROUTE. BR-MD-11-02
 * locks the code, and nothing writes `name` after the insert either — so
 * whatever a currency is created with, it keeps. That is why the Add-currency
 * box asks for a name rather than letting the fallback answer silently.
 */
export const AddCurrencyRequestSchema = z
    .object({
    code: z.string(),
    /** MD-11 field 2: "Maximum 60 characters." Absent = the catalog's, or the code. */
    name: z.string().trim().min(1).max(60).optional(),
    /** MD-11 field 3: "Maximum 5 characters... When empty, the code is used instead." */
    symbol: z.string().trim().min(1).max(5).optional(),
    /** Optional opening manual rate. */
    rate: RateStringSchema.optional(),
    effectiveDate: IsoDateSchema.optional(),
})
    .strict();
export const UpdateCurrencySourceRequestSchema = z
    .object({ source: z.enum(['manual', 'live']) })
    .strict();
export const SetManualRateRequestSchema = z
    .object({
    rate: RateStringSchema,
    /** Defaults to today (BR-FIN-05-14). */
    effectiveDate: IsoDateSchema.optional(),
})
    .strict();
export const FetchRatesResultSchema = z
    .object({
    /** "Rates refreshed at <time>" — the REAL time (defect fixed). */
    refreshedAt: z.string(),
    /** Only rows whose source is Live API (BR-FIN-05-04). */
    updated: z
        .object({ code: z.string(), rate: z.string() })
        .strict()
        .array(),
})
    .strict();
export const RateHistoryPointSchema = z
    .object({
    effectiveDate: IsoDateSchema,
    rate: z.string(),
    source: z.enum(['manual', 'live']),
})
    .strict();
export const SetBaseCurrencyRequestSchema = z.object({ code: z.string().length(3) }).strict();
// ── Revaluation (§5d/§12, F-11) ─────────────────────────────────────────────
const SignedMoneySchema = z.string().regex(/^-?\d{1,13}(\.\d{1,2})?$/);
export const RevaluationRowSchema = z
    .object({
    accountId: z.uuid(),
    code: z.string(),
    name: z.string(),
    /** What decides the sign (BR-FIN-05-09). */
    kind: z.enum(['asset', 'liability']),
    currency: z.string(),
    /** §5d col 2: the balance in the foreign currency. */
    fcBalance: SignedMoneySchema,
    /** §5d col 3: the implied booked rate — fc ÷ booked base, 4 decimals. */
    rateUsed: z.string(),
    /** §5d col 4: today's table rate. */
    currentRate: z.string(),
    /** What the books carry now (base legs). */
    bookedBase: SignedMoneySchema,
    /** §5d col 5: fcBalance ÷ currentRate (BR-FIN-05-08). */
    revaluedBase: SignedMoneySchema,
    /** §5d col 6, signed: + gain, − loss. */
    gainLoss: SignedMoneySchema,
})
    .strict();
export const RevaluationPreviewSchema = z
    .object({
    date: IsoDateSchema,
    rows: RevaluationRowSchema.array(),
    /** §15 Partial + missing rates: derived accounts that cannot revalue, with why. */
    skipped: z
        .object({ accountId: z.uuid(), code: z.string(), currency: z.string(), reason: z.string() })
        .strict()
        .array(),
})
    .strict();
export const PostRevaluationRequestSchema = z
    .object({
    date: IsoDateSchema,
    /** The ticked accounts (BR-FIN-05-10) — at least one (BR-FIN-05-12). */
    accountIds: z.uuid().array().min(1),
    /** Revenue or expense only (BR-FIN-05-11). */
    gainLossAccountId: z.uuid(),
})
    .strict();
export const RevaluationSchema = z
    .object({
    id: z.uuid(),
    /** RV-####. */
    code: z.string(),
    revaluationDate: IsoDateSchema,
    gainLossAccountId: z.uuid(),
    gainLossAccountName: z.string(),
    /** Signed: + gain (zero counts as a gain, §7). */
    total: SignedMoneySchema,
    journalEntryId: z.uuid(),
    postedAt: z.string(),
    lines: RevaluationRowSchema.array(),
})
    .strict();
// ── Router ──────────────────────────────────────────────────────────────────
const currencyParams = z.object({ currencyId: z.uuid() }).strict();
export const fxContract = c.router({
    getCurrencyTable: {
        method: 'GET',
        path: '/api/fx/currencies',
        query: z
            .object({
            /** The MD-11 maintenance screen; every other caller is a picker. */
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: CurrencyTableSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The FIN-05 rate table — base + foreign rows, never the base as a row (accounting.view). Switched-off rows are left out unless includeInactive=true (BR-DATA-06).',
    },
    addCurrency: {
        method: 'POST',
        path: '/api/fx/currencies',
        body: AddCurrencyRequestSchema,
        responses: {
            201: CurrencyRowSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Add a currency from the catalog; duplicates and the base refused (BR-FIN-05-13/-07) (accounting.create).',
    },
    updateCurrencySource: {
        method: 'PATCH',
        path: '/api/fx/currencies/:currencyId',
        pathParams: currencyParams,
        body: UpdateCurrencySourceRequestSchema,
        responses: {
            200: CurrencyRowSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch a row between Manual and Live API (§5c) (accounting.edit).',
    },
    setManualRate: {
        method: 'PUT',
        path: '/api/fx/currencies/:currencyId/rate',
        pathParams: currencyParams,
        body: SetManualRateRequestSchema,
        responses: {
            200: CurrencyRowSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Type a manual rate — refused on a Live API row (ERR-FIN-05-01); stamps "(manual)" (BR-FIN-05-05) (accounting.edit).',
    },
    fetchLiveRates: {
        method: 'POST',
        path: '/api/fx/fetch-rates',
        body: z.object({}).strict().optional(),
        responses: {
            200: FetchRatesResultSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Refresh every Live API row from the provider; manual rows untouched (BR-FIN-05-04) (accounting.edit).',
    },
    /**
     * MD-11 §6's "Switch off" — the row keeps its rates and its history and
     * stops being offered (BR-DATA-06). Hidden on the base currency by the
     * screen, and refused by the server with ERR-MD-11-08's sentence whatever
     * the screen does (BR-MD-11-06).
     */
    deactivateCurrency: {
        method: 'POST',
        path: '/api/fx/currencies/:currencyId/deactivate',
        pathParams: currencyParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: CurrencyRowSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch a currency off (accounting.edit) — MD-11 field 6. The base is refused with ERR-MD-11-08 (BR-MD-11-06).',
    },
    /** MD-11 §7: `Inactive → Active` — the state nothing else moves out of. */
    reactivateCurrency: {
        method: 'POST',
        path: '/api/fx/currencies/:currencyId/reactivate',
        pathParams: currencyParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: CurrencyRowSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch a currency back on (accounting.edit) — the rates and history are untouched.',
    },
    /**
     * §17.3.2's currency row, which had no route at all and therefore no site
     * for ERR-MD-11-07: "deleted only while nothing points at them… If
     * something does, the delete must be refused with a message that names
     * what is in the way" (BR-DATA-03/-04), and the message points at the
     * switch-off above (BR-DATA-05, BR-MD-11-12).
     */
    removeCurrency: {
        method: 'DELETE',
        path: '/api/fx/currencies/:currencyId',
        pathParams: currencyParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a currency (accounting.delete) — only one no document uses and which is not the base (BR-MD-11-12). ERR-MD-11-07 names the count and points at Switch off.',
    },
    getRateHistory: {
        method: 'GET',
        path: '/api/fx/currencies/:currencyId/history',
        pathParams: currencyParams,
        query: z
            .object({ from: IsoDateSchema.optional(), to: IsoDateSchema.optional() })
            .strict(),
        responses: {
            200: z.object({ code: z.string(), points: RateHistoryPointSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Real dated rates for the history chart (the synthetic-chart defect fixed) (accounting.view).',
    },
    setBaseCurrency: {
        method: 'PUT',
        path: '/api/fx/base',
        body: SetBaseCurrencyRequestSchema,
        responses: {
            200: CurrencyTableSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Set the base currency — refused once any document exists (BR-FIN-05-06) (admin.edit).',
    },
    getRevaluationPreview: {
        method: 'GET',
        path: '/api/fx/revaluation',
        query: z.object({ date: IsoDateSchema.optional() }).strict(),
        responses: {
            200: RevaluationPreviewSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The §5d table, derived from every foreign-currency account (Q-FIN-05-3) (accounting.view).',
    },
    postRevaluation: {
        method: 'POST',
        path: '/api/fx/revaluations',
        body: PostRevaluationRequestSchema,
        responses: {
            201: RevaluationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post the §12 entry — P&L one side, foreign accounts the other; R8-locked run recorded (accounting.approve).',
    },
    listRevaluations: {
        method: 'GET',
        path: '/api/fx/revaluations',
        query: pagedQuery({}),
        responses: {
            200: pagedResponse('revaluations', RevaluationSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Posted revaluation history, newest first (accounting.view), PAGED (NFR-42).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=fx.js.map